import { Box, Card, Paper, Typography } from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  BarChart as ChartIcon,
} from '@mui/icons-material'
import { sortBy } from 'ramda'

import { useAppSelector } from 'src/store/hooks'
import { CertificateIcon, UserIcon } from 'src/v3/core/icons'
import {
  selectKpiData,
  selectCeActivities,
  selectCategories,
  selectFilterDescription,
} from '../../index'

export default function SummaryView() {
  // Redux selectors
  let kpiData = useAppSelector(selectKpiData)
  let ceActivities = useAppSelector(selectCeActivities)
  let categories = useAppSelector(selectCategories)
  let filterDescription = useAppSelector(selectFilterDescription)

  let getFilterDescription = () => filterDescription || 'No filters applied'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          background:
            'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <TrendingUpIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              CE Activity Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive overview of continuing education progress and
              engagement
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {getFilterDescription()}
        </Typography>
      </Paper>

      {ceActivities.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TrendingUpIcon
            sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: 'text.secondary' }}
          />
          <Typography variant="h6" sx={{ mb: 2 }}>
            No CE Activities Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No continuing education activities match the current filters.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your time range or removing filters to see more data.
          </Typography>
        </Paper>
      ) : (
        <Box className="space-y-6">
          {/* Engagement Overview */}
          <Box>
            <Paper sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <UserIcon />
                User Engagement Overview
              </Typography>
              <Box className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Box>
                  <Card
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center', height: '100%' }}
                  >
                    <Typography
                      variant="h3"
                      sx={{ mb: 1, color: 'secondary.main' }}
                    >
                      {(kpiData?.avgHoursPerUser || 0).toFixed(1)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Avg Hours/User
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Among active users
                    </Typography>
                  </Card>
                </Box>
                <Box>
                  <Card
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center', height: '100%' }}
                  >
                    <Typography
                      variant="h3"
                      color="success.main"
                      sx={{ mb: 1 }}
                    >
                      {ceActivities.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Total Activities
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      In selected period
                    </Typography>
                  </Card>
                </Box>
                <Box>
                  <Card
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center', height: '100%' }}
                  >
                    <Typography
                      variant="h3"
                      color="success.main"
                      sx={{ mb: 1 }}
                    >
                      {ceActivities.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Total Activities
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      In selected period
                    </Typography>
                  </Card>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Activity Breakdown */}
            <Box>
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CertificateIcon />
                  Activity Types
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(['recorded', 'live', 'external'] as const).map((type) => {
                    let count = ceActivities.filter(
                      (a) => a.activityType === type,
                    ).length
                    let percentage =
                      ceActivities.length > 0
                        ? (count / ceActivities.length) * 100
                        : 0
                    let typeLabel =
                      type === 'recorded'
                        ? 'Recorded Courses'
                        : type === 'live'
                          ? 'Live Classes'
                          : 'External Certificates'
                    let color =
                      type === 'recorded'
                        ? 'primary'
                        : type === 'live'
                          ? 'secondary'
                          : 'success'

                    return (
                      <Box key={type}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">{typeLabel}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {count} ({Math.round(percentage)}%)
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            height: 8,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${percentage}%`,
                              height: '100%',
                              bgcolor: `${color}.main`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Paper>
            </Box>

            {/* Assignment Context */}
            <Box>
              <Paper sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <AssignmentIcon />
                  Assignment Context
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(() => {
                    let assignedCount = ceActivities.filter(
                      (a) => a.wasAssigned,
                    ).length
                    let selfPacedCount = ceActivities.filter(
                      (a) => !a.wasAssigned,
                    ).length
                    let assignedPercentage =
                      ceActivities.length > 0
                        ? (assignedCount / ceActivities.length) * 100
                        : 0
                    let selfPacedPercentage =
                      ceActivities.length > 0
                        ? (selfPacedCount / ceActivities.length) * 100
                        : 0

                    return (
                      <>
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">
                              Assigned Activities
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {assignedCount} ({Math.round(assignedPercentage)}
                              %)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 8,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${assignedPercentage}%`,
                                height: '100%',
                                bgcolor: 'secondary.main',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                        </Box>
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">
                              Self-Paced Learning
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selfPacedCount} (
                              {Math.round(selfPacedPercentage)}
                              %)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 8,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${selfPacedPercentage}%`,
                                height: '100%',
                                bgcolor: 'info.main',
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      </>
                    )
                  })()}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Top Categories */}
          <Box>
            <Paper sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ChartIcon />
                Top CE Categories
              </Typography>
              <Box className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {(() => {
                  let categoryStats = sortBy(
                    (category) => -category.hours,
                    categories.map((category) => ({
                      name: category,
                      count: ceActivities.filter((a) => a.category === category)
                        .length,
                      hours: ceActivities
                        .filter((a) => a.category === category)
                        .reduce((sum, a) => sum + a.ceHours, 0),
                    })),
                  ).slice(0, 6)

                  return categoryStats.map((category) => (
                    <Box key={category.name}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1, fontWeight: 600 }}
                        >
                          {category.name}
                        </Typography>
                        <Typography
                          variant="h6"
                          color="primary.main"
                          sx={{ mb: 0.5 }}
                        >
                          {category.hours}h
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {category.count} activities
                        </Typography>
                      </Card>
                    </Box>
                  ))
                })()}
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  )
}
